import { getClientRefFromParsed } from '@/components/chisai/GCB2/helpers'

/**
 * ОПЗ 2.0 по макету DH_OPZ_2_0_updated. Страница показывает выдачу
 * аналитической функции front_get_same_day_rec_report как есть
 * (/GCB2/sameDayRecReport/opz2/v3): строки и итоги не фильтруются,
 * не схлопываются и не пересчитываются. Здесь — только подписи и форматирование.
 *
 * Поле, которого нет в выдаче функции, показывается прочерком. Имена полей
 * задуманы заранее (opz2-function-request.md): как только аналитик добавит
 * поле в выдачу, оно появится на странице без релиза приложения.
 */

export type Status = 'done' | 'plan' | 'cancel'
/** Значения flag_cross_sail функции. */
export type CrossFlag = 'all' | 'same_day' | 'future_day' | 'firstly'
/** Значения flag_record_same_staff функции. */
export type StaffFlag = 'all' | 'same_staff' | 'other_staff'
export type FunctionStatus = 'success' | 'future' | 'cancel'

/** Строка или итог выдачи функции — поля как их назвал аналитик. */
export type Row = Record<string, any>

export interface Report {
    period: { from: string; to: string }
    /** applied_filter функции: какие фильтры она применила */
    appliedFilter: Row
    /** stat_by_date_data / total_stat_data */
    days: { rows: Row[]; total: Row }
    /** stat_by_staff / stat_by_staff_total — по всем ОПЗ */
    staffStat: { rows: Row[]; total: Row }
    /** same_spec_details / stat_by_staff_total с фильтром flag_record_same_staff */
    staffSelection: { flag: StaffFlag; appliedFilter: Row; rows: Row[]; total: Row }
    /** cross_sail_table / cross_sail_total */
    cross: { flag: CrossFlag; rows: Row[]; total: Row }
    /** kpi_opz / kpi_opz_total */
    kpi: { rows: Row[]; total: Row; configured: boolean; schemeName?: string | null }
    adminId: string | null
    staff: Record<string, string>
    clients: Record<string, { name: string | null; hrefInfo: any }>
    accounts: Record<string, { userId: string; name: string | null; email: string | null }>
    crmAdmins: Record<string, { name: string | null; email: string | null }>
}

export const EMPTY_REPORT: Report = {
    period: { from: '', to: '' },
    appliedFilter: {},
    days: { rows: [], total: {} },
    staffStat: { rows: [], total: {} },
    staffSelection: { flag: 'all', appliedFilter: {}, rows: [], total: {} },
    cross: { flag: 'all', rows: [], total: {} },
    kpi: { rows: [], total: {}, configured: false },
    adminId: null,
    staff: {},
    clients: {},
    accounts: {},
    crmAdmins: {},
}

// ---------------------------------------------------------------------------
// Форматирование — как в макете

const numberFormat = new Intl.NumberFormat('ru-RU')
const percentFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 })

export const num = (value: number) => numberFormat.format(value)

const toDate = (day: string) => new Date(`${day}T12:00:00Z`)
export const shortDate = (day: string) =>
    toDate(day).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        timeZone: 'UTC',
    })
export const longDate = (day: string) =>
    toDate(day)
        .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
        .replace(' г.', '')

export function periodLabel(from: string, to: string) {
    if (!from || !to) return ''
    if (from === to) return longDate(from)
    if (from.slice(0, 7) === to.slice(0, 7)) return `${toDate(from).getUTCDate()}–${longDate(to)}`
    return `${shortDate(from)} — ${shortDate(to)}`
}

export const addDays = (day: string, amount: number) =>
    new Date(toDate(day).getTime() + amount * 86400000).toISOString().slice(0, 10)

/** Пресеты периода из макета. */
export function presetRange(preset: 'week' | 'month' | 'previous' | 'quarter', today: string) {
    const now = toDate(today)
    if (preset === 'week') {
        const from = addDays(today, -((now.getUTCDay() + 6) % 7))
        return { from, to: addDays(from, 6) }
    }
    const y = now.getUTCFullYear()
    const m = now.getUTCMonth()
    const iso = (d: Date) => d.toISOString().slice(0, 10)
    if (preset === 'month')
        return { from: iso(new Date(Date.UTC(y, m, 1))), to: iso(new Date(Date.UTC(y, m + 1, 0))) }
    if (preset === 'previous')
        return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) }
    return { from: iso(new Date(Date.UTC(y, m - 3, 1))), to: iso(new Date(Date.UTC(y, m, 0))) }
}

export const STATUS_LABEL: Record<Status, string> = { done: 'Завершён', plan: 'Ожидается', cancel: 'Отменён' }

/** Статус записи из функции -> подпись страницы. */
export const FUNCTION_STATUS: Record<FunctionStatus, Status> = {
    success: 'done',
    future: 'plan',
    cancel: 'cancel',
}

export const DASH = '—'

/** Есть ли поле в выдаче функции. */
export const has = (value: unknown) => value !== undefined && value !== null && value !== ''

/** Число из выдачи функции; поля нет — прочерк. */
export const cnt = (value: unknown) => (has(value) ? num(Number(value)) : DASH)

/** Процент, который отдала функция; поля нет — прочерк. */
export const fnPct = (value: unknown) => (has(value) ? `${percentFormat.format(Number(value))}%` : DASH)

/** Сумма в рублях из выдачи функции; поля нет — прочерк. */
export const money = (value: unknown) => (has(value) ? `${num(Math.round(Number(value)))} ₽` : DASH)

/** Признак из выдачи функции; поля нет — прочерк. */
export const yesNo = (value: unknown) => (has(value) ? (value ? 'Да' : 'Нет') : DASH)

/** Список из выдачи функции (массив или строка); поля нет — прочерк. */
export const list = (value: unknown) =>
    Array.isArray(value) ? value.filter(Boolean).join(', ') || DASH : has(value) ? String(value) : DASH

/** Применила ли функция фильтр: она возвращает его в applied_filter. */
export const filterApplied = (applied: Row | undefined, key: string, value: unknown) =>
    has(applied?.[key]) && String(applied?.[key]) === String(value)

export const staffLabel = (report: Report, staffId: string | null | undefined) =>
    has(staffId) ? report.staff[String(staffId)] || String(staffId) : DASH

/**
 * Администратор из opz_creating_user_id: сопоставленный — пользователь DH,
 * иначе имя из YCLIENTS. Служебные значения функции («Нет данных») — как есть.
 */
export function adminLabel(report: Report, id: string | null | undefined) {
    if (!id) return '—'
    const account = report.accounts[id]
    if (account) return account.email || account.name || id
    if (!/^\d+$/.test(id)) return id
    const crm = report.crmAdmins[id]
    return crm?.name || crm?.email || `ID ${id}`
}

/** Ссылка на клиента в YCLIENTS из href_info (массив или JSON-строка). */
export function yclientsUrl(hrefInfo: any): string | null {
    let items = hrefInfo
    if (typeof items === 'string') {
        try {
            items = JSON.parse(items)
        } catch (e) {
            return null
        }
    }
    const item = Array.isArray(items) ? items.find((el: any) => el?.connectorType === 'yClients') : null
    return item ? getClientRefFromParsed(item) ?? null : null
}

export function clientLabel(report: Report, clientId: string | null | undefined) {
    if (!clientId) return '—'
    return report.clients[clientId]?.name || clientId
}

// ---------------------------------------------------------------------------
// Сортировка и пагинация таблиц, как в макете

export interface Sort {
    field: string
    dir: 1 | -1
}

export function sorted<T extends Record<string, any>>(rows: T[], sort?: Sort | null): T[] {
    if (!sort) return rows
    return [...rows].sort((a, b) => {
        const v1 = a[sort.field]
        const v2 = b[sort.field]
        return (
            (typeof v1 === 'number' ? v1 - v2 : String(v1 ?? '').localeCompare(String(v2 ?? ''), 'ru')) *
            sort.dir
        )
    })
}

export function paginate<T>(rows: T[], page: number, size = 8) {
    const pages = Math.max(1, Math.ceil(rows.length / size))
    const p = Math.min(Math.max(1, page), pages)
    return {
        items: rows.slice((p - 1) * size, p * size),
        page: p,
        pages,
        start: rows.length ? (p - 1) * size + 1 : 0,
        end: Math.min(p * size, rows.length),
        count: rows.length,
    }
}
